import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { PageType } from '@vgtv/api-client/lib/page';

import {
  getAssetPageMetadata,
  getCategoryPageMetadata,
  getCustomPageMetadata,
  getDefaultMetadata,
  getStoryPageMetadata,
  getTagPageMetadata,
} from '../../shared/helpers/getMetadata';
import { config } from '../../config';
import { useVendor } from '../VendorPovider/VendorPovider';

import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { CnpTag } from '@vgtv/api-client/lib/cnp_tag';
import type { CnpStory } from '@vgtv/api-client/lib/cnp_story';

type Props = { overrides?: any } & (
  | {
      entity: SvpCategoryAdditional;
      pageType: PageType.CATEGORY;
    }
  | {
      entity: SvpAssetAdditional;
      pageType: 'asset';
    }
  | {
      entity: CnpTag;
      pageType: PageType.TAG;
    }
  | { entity: CnpStory; pageType: PageType.STORY }
  | {
      entity: { slug: string };
      pageType: PageType.CUSTOM | PageType.STATIC;
    }
);

function PageMetadata({ entity, pageType, overrides }: Props) {
  const { vendor } = useVendor();

  const metadata = useMemo(() => {
    if (pageType === 'asset') {
      return getAssetPageMetadata(config.provider, entity);
    }

    if (pageType === PageType.CATEGORY) {
      return getCategoryPageMetadata({
        provider: config.provider,
        vendor,
        category: entity,
        overrides,
      });
    }

    if (pageType === PageType.TAG) {
      return getTagPageMetadata(config.provider, entity, overrides);
    }

    if (pageType === PageType.STORY) {
      return getStoryPageMetadata(config.provider, entity, overrides);
    }

    if (pageType === PageType.CUSTOM) {
      return getCustomPageMetadata(config.provider, entity.slug, overrides);
    }

    return getDefaultMetadata(config.provider);
  }, [pageType, entity, overrides, vendor]);

  return (
    <Helmet>
      <title>{metadata.title}</title>
      <meta name="robots" content={metadata.robots} />
      <meta name="description" content={metadata.description} />
      <meta name="keywords" content={metadata.keywords} />
      <link rel="canonical" href={metadata.canonical} />
      {metadata.openGraph &&
        (
          Object.keys(
            metadata.openGraph
          ) as unknown as (keyof typeof metadata.openGraph)[]
        ).map((key) => (
          <meta property={key} content={metadata.openGraph[key]} key={key} />
        ))}
    </Helmet>
  );
}

export { PageMetadata };
